import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption<T extends string> {
  key: T;
  label: React.ReactNode;
  disabled?: boolean;
}

type Variant = 'ghost' | 'outline';
type Size = 'sm' | 'md' | 'lg';

/** Generic dropdown picker. Replaces native <select> with a styled
 *  popover so chevrons + active states match the rest of the UI.
 *  Closes on outside-click and Escape. Generic over the option key. */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  variant = 'ghost',
  size = 'md',
  placeholder,
  className = '',
  fullWidth = false,
}: {
  value: T | null;
  options: DropdownOption<T>[];
  onChange: (next: T) => void;
  variant?: Variant;
  size?: Size;
  placeholder?: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = options.find((o) => o.key === value);

  // Trigger styling per variant + size.
  const triggerSize: Record<Size, string> = {
    sm: 'h-8 px-2.5 text-[12px]',
    md: 'h-9 px-3 text-[13px]',
    lg: 'h-11 px-3 text-[14px]',
  };
  const triggerStyle: React.CSSProperties =
    variant === 'outline'
      ? {
          border: '1px solid var(--pro-border)',
          background: 'white',
          color: 'var(--pro-text-dark)',
          fontFamily: 'Figtree',
        }
      : {
          color: 'var(--pro-text-dark)',
          background: open ? 'var(--pro-grey-subtle)' : 'transparent',
          fontFamily: 'Figtree',
        };

  return (
    <div
      ref={ref}
      className={`relative inline-block ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex items-center justify-between gap-2 rounded-lg font-medium ${triggerSize[size]} ${
          fullWidth ? 'w-full' : ''
        }`}
        style={triggerStyle}
      >
        <span className="truncate">{current?.label ?? placeholder ?? '—'}</span>
        <ChevronDown
          className="w-[14px] h-[14px] shrink-0"
          style={{ color: 'var(--pro-text-grey)' }}
        />
      </button>
      {open && (
        <div
          role="listbox"
          className={`absolute right-0 mt-1 ${
            fullWidth ? 'left-0 right-auto w-full' : 'min-w-[160px]'
          } rounded-lg z-20 py-1 pro-scale-in`}
          style={{
            background: 'white',
            border: '1px solid var(--pro-border)',
            boxShadow: '0 8px 24px rgba(13,13,18,0.08)',
          }}
        >
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              role="option"
              aria-selected={opt.key === value}
              onClick={() => {
                if (opt.disabled) return;
                onChange(opt.key);
                setOpen(false);
              }}
              disabled={opt.disabled}
              className="w-full text-left px-3 py-2 text-[13px] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                color: opt.key === value ? 'var(--pro-primary)' : 'var(--pro-text-dark)',
                background:
                  opt.key === value ? 'var(--pro-primary-subtle)' : 'transparent',
                fontFamily: 'Figtree',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
